// Cold-storage wording on the floor screens (F07-FLOOR:WORDS, ui-flow BigPack).
//
// The rate is R per COLDROOM per SAST day the room holds a pallet, split
// between the pallets in it that day, and only storage clients (pallets on a
// Third-party storage receipt) are billed their share. The invoice reads that
// rule (cold_storage_for_grower, mig 20261033200000) and so does the floor's
// cold_storage_revenue view (mig 20261035100000). No sentence here may read as
// pallets x days x rate: that per-pallet rule is gone, and a card that states
// it beside a room-day figure misleads whoever checks the sum.
//
// Rates come in already FORMATTED (`money(rate)` / `fmtMoneyAuto(rate)`), so a
// screen under the money lock still masks them. Nothing here formats rand.

import { isExternalReceiptRun } from './externalReceiptPallets'

/** The rule after the rate: "R 315.00 per coldroom per day, split between the pallets in the room". */
export function roomDayRule(rate: string): string {
  return `${rate} per coldroom per day, split between the pallets in the room`
}

/** Coldroom → "Cold-storage rental accruing": what the total under it is. */
export function coldroomAccruingLine(rate: string): string {
  return `Storage clients' share of room-days at ${rate} per coldroom per day · own fruit not billed`
}

/** Coldroom report → the "Accrued cost" tile: what the sum is (its Cost column is the view's revenue, never days x rate). */
export const ACCRUED_COST_NOTE = "storage clients' share of room-days · own fruit not billed"

/**
 * Who pays a pallet's cold storage. `client`: a Third-party storage receipt.
 * `own`: our fruit, received packed off-site. `packed-here`: our own
 * production — no receipt, or a run that is not an external receipt (the view
 * bills only EXTERNAL_RECEIPT runs, whatever receipt row a PACK run carries).
 * `unknown`: the receipt read has not answered, so the card must not say either.
 */
export type StoragePayer = 'client' | 'own' | 'packed-here' | 'unknown'

/**
 * The payer from the pallet's receipt read (`undefined` = not read yet, `null`
 * = no receipt) and its run's kind (`undefined` = not known yet).
 */
export function storagePayer(
  receipt: { ownership: string } | null | undefined,
  runKind?: string | null,
): StoragePayer {
  if (runKind !== undefined && !isExternalReceiptRun(runKind)) return 'packed-here'
  if (receipt === undefined) return 'unknown'
  if (receipt === null) return 'packed-here'
  return receipt.ownership === 'THIRD_PARTY' ? 'client' : 'own'
}

/**
 * PalletDetail → Cold storage: the pallet's room-day share and the rule, one line.
 *
 * The share comes back from the view's own figure: for a billed pallet
 * revenue = round(share x rate, 2), so revenue / rate returns the share to
 * within 0.005 / rate, inside the 2 dp shown (the invoice's Room-days column
 * is 2 dp too). A revenue above 0 means the view billed it, whatever the
 * receipt read says. Returns null when there is no rate: the card says
 * "no rate set" instead.
 */
export function palletRoomDayLine(
  { revenue, rate, rateText, payer }: { revenue: number; rate: number; rateText: string; payer: StoragePayer },
): string | null {
  if (!(rate > 0)) return null
  const rule = roomDayRule(rateText)
  if (revenue > 0 || payer === 'client') {
    const share = (Math.max(0, revenue) / rate).toFixed(2)
    return `${share} room-day${share === '1.00' ? '' : 's'} at ${rule}`
  }
  if (payer === 'own')         return `Not billed (own fruit) · storage clients pay ${rule}`
  if (payer === 'packed-here') return `Not billed (packed here) · storage clients pay ${rule}`
  return `Storage clients pay ${rule} · own fruit is not billed`
}
